import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import api from "../../../api";
import { setMessage } from "../../../store/Page";
import CampaignSMSForm from "./CampaignSMSForm";
import SMSCampaignDetails from "./SMSCampaignDetails";
import SmsCampaignStatusBadge from "./SmsCampaignStatusBadge";
import { SmsCampaignTypeBadge } from "./SmsCampaignTypeBadge";
import SmsCampaignActionsDropdown from "./SmsCampaignActionsDropdown";
import { Segment, Table, Pagination } from "semantic-ui-react";
import { DatatablePageSizeSelect } from "../CompletedChats/DatatablePageSizeSelect";
import ConfirmationModal from "../Workspace/Botbuilder/Details/confusedbranch/DataConnect/ConfirmationModal";
import GlobalMessage from "../TrustHub/GlobalMessage";
import "./OutboundSMSCampaignList.css";

const APPROVED_STATUSES = ['approved', 'twilio-approved', 'verified'];

const isApprovedStatus = (status) => APPROVED_STATUSES.includes(status);

const tableHeader = [
  { title: "Campaign Name", width: "3", sort: "name" },
  { title: "Created Date", width: "2", sort: "created_at" },
  { title: "Campaign Type", width: "2" },
  { title: "Campaign Category", width: "2" },
  { title: "Send Type", width: "2" },
  { title: "Schedule Type", width: "2" },
  { title: "Start Campaign Date", width: "2", sort: "start_date" },
  { title: "Next Campaign Date", width: "2" },
  { title: "Start Campaign Time", width: "2" },
  { title: "End Campaign Time", width: "2" },
  { title: "Status", width: "2", sort: "status" },
  { title: "Total Customers", width: "2" },
  { title: "Workspace Name", width: "2" },
  { title: "Outbound SMS Number", width: "2" },
  { title: "Actions", width: "2" },
];

const OutboundSMSCampaignList = (props) => {
  const dispatch = useDispatch();
  const [complianceData, setComplianceData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignListLoading, setCampaignListLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [viewCampaign, setViewCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const pollingIntervalRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (props.user?.user_id) {
      fetchComplianceStatus();
      loadCampaignsList();

      return () => {
        isMountedRef.current = false;
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user?.user_id]);

  // ensure mounted flag is cleared on unmount in all cases
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchComplianceStatus = async (silent = false) => {
    try {
      if (!silent && isMountedRef.current) setLoading(true);
      if (!props.user?.user_id) return;

      const res = await api.outbound.getSmsComplianceStatus(parseInt(props.user?.user_id, 10));
      if (res?.status !== false && res?.data) {
        if (isMountedRef.current) {
          setComplianceData(res.data);
          setError(null);
        }
      } else if (res?.status !== false && res && !res.data) {
        if (isMountedRef.current) {
          setComplianceData(res);
          setError(null);
        }
      } else {
        if (isMountedRef.current) setError(res?.message || 'Failed to fetch compliance status');
      }
    } catch (e) {
      console.error('Failed to fetch compliance status:', e);
      if (isMountedRef.current) setError('Network error occurred');
    } finally {
      if (!silent && isMountedRef.current) setLoading(false);
    }
  };

  const formatStatus = (status) => {
    if (!status) return '';
    if (status === 'approved' || status === 'twilio-approved') return 'Completed';
    if (status === 'non_marketing') return 'General Non-Marketing';

    return status
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

   const getSendTypeDisplay = (sendType) => {
    switch (sendType) {
      case 'now':
        return 'Send Now';
      case 'scheduled':
        return 'Scheduled';
      default:
        return sendType || 'N/A';
    }
  };

   const displayScheduleType = (schedule_type) => {
    switch (schedule_type) {
      case 'one_time':
        return 'One Time';
      case 'ongoing':
        return 'Ongoing';
      default:
        return schedule_type || 'N/A';
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const parts = String(timeStr).split(':');
    if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    return timeStr;
  };

  const loadCampaignsList = async (silent = false, search = searchTerm, overrides = {}) => {
    try {
      if (!silent && isMountedRef.current) setCampaignListLoading(true);
      const params = {
        page: overrides.page !== undefined ? overrides.page : currentPage,
        limit: overrides.limit !== undefined ? overrides.limit : pageSize,
        sort_column: overrides.sort_column !== undefined ? overrides.sort_column : sortColumn,
        sort_direction: overrides.sort_direction !== undefined ? overrides.sort_direction : sortDirection,
        search: search || '',
      };

      const res = await api.outbound.getSmsCampaignsList(params);
      
      if (res?.status !== false && res?.data) {
        if (isMountedRef.current) {
          setCampaigns(res.data.campaigns || []);
          if (res.data.pagination) {
            setTotalPages(parseInt(res.data.pagination.last_page) || 0);
            setTotalCount(parseInt(res.data.pagination.total) || 0);
          } else {
            setTotalPages(0);
            setTotalCount(0);
          }
        }
      } else {
        if (isMountedRef.current) {
          setCampaigns([]);
          setTotalPages(0);
          setTotalCount(0);
        }
      }
    } catch (e) {
      console.error('Failed to load campaigns:', e);
      if (isMountedRef.current) setCampaigns([]);
    } finally {
      if (isMountedRef.current) setCampaignListLoading(false);
    }
  };

  const handleSort = (columnName) => {
    let newDirection = "asc";
    if (sortColumn === columnName) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortColumn(columnName);
    setSortDirection(newDirection);
    setCurrentPage(1);
    
    loadCampaignsList(false, searchTerm, {
      page: 1,
      sort_column: columnName,
      sort_direction: newDirection,
    });
  };

  const onChangePage = (event, data) => {
    setCurrentPage(data.activePage);
    loadCampaignsList(false, searchTerm, {
      page: data.activePage,
    });
  };

  const onChangeLimit = (event, data) => {
    if (data.value !== pageSize) {
      setPageSize(data.value);
      setCurrentPage(1);
      loadCampaignsList(false, searchTerm, {
        page: 1,
        limit: parseInt(data.value, 10),
      });
    }
  };

  // compliance/sms-status shape: { brand, campaign, numbers, compliance, profile? }
  const brand = complianceData?.brand;
  const campaign = complianceData?.campaign;
  const numbers = complianceData?.numbers;
  const compliance = complianceData?.compliance;

  const profileStatus =
    complianceData?.profile_status
    || complianceData?.profile?.status
    || props.profileStatus;

  const brandExists = Boolean(brand?.exists);
  const campaignExists = Boolean(campaign?.exists);

  const brandStatus = brandExists ? (brand?.status || 'not_created') : 'not_created';
  const campaignStatus = campaignExists ? (campaign?.status || 'not_created') : 'not_created';

  const hasAssignedNumber = Boolean(
    numbers?.has_assigned || (Number(numbers?.assigned_count) > 0)
  );

  const isProfileComplete = profileStatus === 'twilio-approved';
  const isBrandComplete = Boolean(brand?.approved) || (brandExists && isApprovedStatus(brandStatus));
  const isCampaignComplete = Boolean(campaign?.approved) || (campaignExists && isApprovedStatus(campaignStatus));

  const canCreateSMSCampaign = Boolean(compliance?.can_create_campaign);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (value.length >= 2 || value.length === 0) {
        setCurrentPage(1);
        loadCampaignsList(false, value);
      }
    }, 500);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setCurrentPage(1);
    loadCampaignsList(false, searchTerm);
  };

  const handleAddCampaign = () => {
    if (!canCreateSMSCampaign) return;
    setEditingCampaign(null);
    setShowForm(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  const handleViewCampaign = (campaign) => {
    setViewCampaign(campaign);
  };

  const handleBackFromView = () => {
    setViewCampaign(null);
    loadCampaignsList();
  };

  const handleBackToList = () => {
    setShowForm(false);
    setEditingCampaign(null);
    loadCampaignsList();
  };

  const handleSaveCampaign = async (campaignData) => {
    const isEditing = !!editingCampaign?.id;
    const { voice_sources: voiceSources, ...campaignPayload } = campaignData;

    const res = isEditing
      ? await api.outbound.updateSmsCampaign({
          campaign_id: editingCampaign.id,
          ...campaignPayload,
        })
      : await api.outbound.storeSmsCampaign(campaignPayload);

    if (res?.status) {
      const campaignId = isEditing ? editingCampaign.id : res?.data?.id;

      // Decoupled follow-up: voice sources save separately (own endpoint) so a failure here
      // doesn't undo an otherwise-successful campaign save. But a failure here must NOT be
      // reported as overall success -- the user asked for specific voice sources and didn't
      // get them, so keep the form open with the real error instead of closing it on a lie.
      if (campaignPayload.type === "triggered" && voiceSources && campaignId) {
        const sourcesRes = await api.outbound.saveSmsCampaignVoiceSources(campaignId, voiceSources);
        if (!sourcesRes?.status) {
          if (!isEditing) {
            // The campaign itself now exists -- point the still-open form at it so a retry
            // updates this campaign instead of creating a duplicate one.
            setEditingCampaign(res?.data || { id: campaignId });
          }
          dispatch(setMessage({
            type: "error",
            text: sourcesRes?.message || "Campaign saved, but voice sources could not be saved. Please try again.",
          }));
          return {
            status: false,
            message: sourcesRes?.message || "Campaign saved, but voice sources could not be saved. Please try again.",
          };
        }
      }

      dispatch(setMessage({
        type: "success",
        text: isEditing
          ? "Campaign updated successfully!"
          : "Campaign created successfully!",
      }));
      handleBackToList();
      return { status: true };
    }
    dispatch(setMessage({
      type: "error",
      text: res?.message || (isEditing ? "Failed to update campaign." : "Failed to save campaign."),
    }));
    return res || { status: false, message: "Failed to save campaign." };
  };

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete) return;
    
    try {
      setCampaignListLoading(true);
      const res = await api.outbound.deleteSmsCampaign(campaignToDelete.id);
      if (res?.status) {
        dispatch(setMessage({
          type: "success",
          text: "Campaign deleted successfully!"
        }));
        loadCampaignsList();
      } else {
        dispatch(setMessage({
          type: "error",
          text: res?.message || 'Failed to delete campaign.'
        }));
      }
    } catch (e) {
      console.error('Error deleting campaign:', e);
      dispatch(setMessage({
        type: "error",
        text: 'Failed to delete campaign.'
      }));
    } finally {
      setDeleteModalOpen(false);
      setCampaignToDelete(null);
      setCampaignListLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setCampaignToDelete(null);
  };

  const renderTableHeader = () => {
    return (
      <Table.Header>
        <Table.Row>
          {tableHeader.map((header, index) => (
            <Table.HeaderCell
              key={index}
              style={{ cursor: header.sort ? "pointer" : "default" }}
              onClick={() => header.sort && handleSort(header.sort)}
            >
              {header.title}
              {header.sort && (
                <i
                  className={`fa fa-sort-${sortColumn === header.sort
                    ? sortDirection === "asc"
                      ? "up"
                      : "down"
                    : ""
                    }`}
                  style={{ marginLeft: "5px" }}
                />
              )}
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
    );
  };

  const renderTableBody = () => {
    if (!campaigns || campaigns.length === 0) {
      return (
        <Table.Body>
          <Table.Row>
            <Table.Cell colSpan={tableHeader.length} textAlign="center">
              No record found.
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      );
    }

    return (
      <Table.Body>
        {campaigns.map((campaign) => (
          <Table.Row key={campaign.id}>
            <Table.Cell>{campaign.name || campaign.campaign_name || 'N/A'}</Table.Cell>
            <Table.Cell>{campaign.created_at || 'N/A'}</Table.Cell>
            <Table.Cell>
              <SmsCampaignTypeBadge type={campaign.type} />
            </Table.Cell>
            <Table.Cell>{formatStatus(campaign.campaign_category) || 'N/A'}</Table.Cell>
            <Table.Cell>{getSendTypeDisplay(campaign.send_type)}</Table.Cell>
            <Table.Cell>{displayScheduleType(campaign.schedule?.schedule_type)}</Table.Cell>
            <Table.Cell>{campaign.schedule?.start_date || 'N/A'}</Table.Cell>
            <Table.Cell>{campaign.schedule?.next_run_date || campaign.next_execution_date || 'N/A'}</Table.Cell>
            <Table.Cell>{formatTime(campaign.schedule?.start_time)}</Table.Cell>
            <Table.Cell>{formatTime(campaign.schedule?.end_time)}</Table.Cell>
            <Table.Cell>
              <SmsCampaignStatusBadge status={campaign.status} />
            </Table.Cell>
            <Table.Cell>{campaign.customers?.length || 0}</Table.Cell>
            <Table.Cell>{campaign.workspace_name || 'N/A'}</Table.Cell>
            <Table.Cell>{campaign.outbound_number?.phone || 'N/A'}</Table.Cell>
            <Table.Cell>
              <SmsCampaignActionsDropdown
                campaign={campaign}
                handleViewCampaign={handleViewCampaign}
                handleEditCampaign={handleEditCampaign}
                onStatusChanged={(res) => {
                  if (res?.status) {
                    dispatch(setMessage({
                      type: "success",
                      text: res.message || "Campaign status updated successfully.",
                    }));
                    loadCampaignsList();
                  } else if (res?.message) {
                    dispatch(setMessage({
                      type: "error",
                      text: res.message,
                    }));
                  }
                }}
                onWorkingChange={(isWorking) => setCampaignListLoading(isWorking)}
                profileStatus={profileStatus}
                campaignProfileStatus={campaignStatus}
              />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        width: '100%',
      }}>
        <div className="ui active inline loader"></div>
      </div>
    );
  }

  const brandBtnEnabled = isProfileComplete && !brandExists;
  const campaignBtnEnabled =
    isProfileComplete && brandExists && isBrandComplete && !campaignExists;
  const phoneBtnEnabled =
    isProfileComplete
    && brandExists
    && campaignExists
    && isBrandComplete
    && isCampaignComplete
    && !hasAssignedNumber;

  const goToTrustHub = (path) => {
    window.location.href = path;
  };

  const smsActionBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    lineHeight: 1.2,
  };

  const smsActionIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    fontSize: '1.05em',
    paddingTop: '1px',
  };

  const renderStatusAndActions = () => (
    <>
      <GlobalMessage />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          padding: '0 20px',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <form
          id="searchsmscampaign"
          method="post"
          onSubmit={handleSearchSubmit}
          style={{ margin: 0 }}
        >
          <div className="kt-input-icon kt-input-icon--right header-search kt-input-icon--right">
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              id="generalSearch"
              name="q"
              value={searchTerm || ''}
              onChange={handleSearchChange}
            />
            <span className="kt-input-icon__icon kt-input-icon__icon--right">
              <span><i className="la la-search"></i></span>
            </span>
          </div>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginLeft: 'auto' }}>
          {error && (
            <span style={{ color: '#d9534f', fontSize: '14px' }}>
              {error}
              <button
                type="button"
                onClick={() => fetchComplianceStatus()}
                style={{
                  marginLeft: '8px',
                  padding: '4px 10px',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Retry
              </button>
            </span>
          )}

          {canCreateSMSCampaign && (
            <button
              type="button"
              className="linear-gradient yr-submitbtn btn-sm"
              style={smsActionBtnStyle}
              onClick={handleAddCampaign}
            >
              <i className="la la-plus" style={smsActionIconStyle} aria-hidden />
              Add SMS Campaign
            </button>
          )}
        </div>
      </div>

      {(!brandExists || !campaignExists || !hasAssignedNumber) && (
        <div
          className="outbound-sms-compliance-actions"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: '10px',
            padding: '0 20px',
            gap: '12px',
          }}
        >
          {!brandExists && (
          <button
            type="button"
            className="linear-gradient yr-submitbtn btn-sm"
            style={smsActionBtnStyle}
            disabled={!brandBtnEnabled}
            title={
              !isProfileComplete
                ? 'Complete your Trust Hub profile first'
                : 'Register your A2P brand in Trust Hub'
            }
            onClick={() => brandBtnEnabled && goToTrustHub('/trust-hub?tab=sms')}
          >
            <i className="la la-plus" style={smsActionIconStyle} aria-hidden />
            Add Brand
          </button>
          )}
          {!campaignExists && (
          <button
            type="button"
            className="linear-gradient yr-submitbtn btn-sm"
            style={smsActionBtnStyle}
            disabled={!campaignBtnEnabled}
            title={
              !isProfileComplete
                ? 'Complete your Trust Hub profile first'
                : !brandExists
                  ? 'Add and approve a brand first'
                  : !isBrandComplete
                    ? 'Wait for brand approval or fix brand registration'
                    : 'Register your A2P campaign in Trust Hub'
            }
            onClick={() => campaignBtnEnabled && goToTrustHub('/trust-hub?tab=sms')}
          >
            <i className="la la-plus" style={smsActionIconStyle} aria-hidden />
            Add A2P Campaign
          </button>
          )}
          {!hasAssignedNumber && (
          <button
            type="button"
            className="linear-gradient yr-submitbtn btn-sm"
            style={smsActionBtnStyle}
            disabled={!phoneBtnEnabled}
            title={
              !isProfileComplete
                ? 'Complete your Trust Hub profile first'
                : !isBrandComplete
                  ? 'Complete brand registration first'
                  : !isCampaignComplete
                    ? 'Complete A2P campaign registration first'
                    : 'Assign SMS numbers in Trust Hub'
            }
            onClick={() => phoneBtnEnabled && goToTrustHub('/trust-hub?tab=phones&subtab=sms')}
          >
            <i className="la la-phone" style={smsActionIconStyle} aria-hidden />
            Assign Phone Numbers
          </button>
          )}
        </div>
      )}
      {/* SMS Campaigns List Table */}
      <div style={{ marginTop: '1em' }}>
        {campaignListLoading && <div className="ui loading form"></div>}
        <Segment style={{ width: "calc(100vw - 100px)", overflowX: "auto" }}>
          <Table ui celled selectable sortable table>
            {renderTableHeader()}
            {renderTableBody()}
          </Table>
        </Segment>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DatatablePageSizeSelect
              limit={pageSize.toString()}
              onChangeLimit={onChangeLimit}
            />
            <span style={{ marginLeft: '10px' }}>
              Showing {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1} - {Math.min(totalCount, currentPage * pageSize)} of {totalCount}
            </span>
          </div>
          <div>
            <Pagination
              totalPages={Math.max(1, totalPages)}
              activePage={currentPage}
              onPageChange={onChangePage}
            />
          </div>
        </div>
      </div>
    </>
  );

  if (viewCampaign) {
    return (
      <div style={{ padding: '0 20px', marginTop: '20px' }}>
        <SMSCampaignDetails
          campaign_id={viewCampaign.id}
          user_id={props.user?.user_id}
          onBackToList={handleBackFromView}
        />
      </div>
    );
  }

  if (showForm) {
    return (
      <div style={{ padding: '0 20px', marginTop: '20px' }}>
        <CampaignSMSForm
          user_id={props.user?.user_id}
          campaign_id={editingCampaign?.id}
          editingCampaign={editingCampaign}
          onSave={handleSaveCampaign}
          onCancel={handleBackToList}
          compactMode={true}
        />
      </div>
    );
  }

  return (
    <>
      {renderStatusAndActions()}
      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        title="Delete Campaign"
        content={`Are you sure you want to delete the campaign "${campaignToDelete?.name || campaignToDelete?.campaign_name || ""}"?`}
        cancelLabel="No"
        confirmLabel="Yes"
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default OutboundSMSCampaignList;
